"use client";

import { useEffect, useState, type ReactNode } from "react";

/**
 * Renders its children only AFTER the first real user interaction
 * (scroll / pointer / key / touch / mouse move), with a timer backstop so
 * children still mount for users who never interact.
 *
 * Used to keep non-critical third-party tags (GTM, Google Ads, Meta Pixel,
 * RudderStack, Microsoft UET, Microsoft Clarity) out of the first-paint / LCP
 * window — their download + main-thread execution were inflating LCP render
 * delay on slow mobile connections.
 *
 * TRADEOFF: a visitor who lands and leaves WITHOUT any interaction before the
 * backstop fires is not tracked. This is the standard "load marketing tags on
 * interaction" pattern. To revert to immediate loading, pass `eager`.
 */
export default function DeferUntilInteraction({
  children,
  // Back to the original 4,500ms. The 20,000ms experiment is described in gtmSnippet.ts:
  // it bought a large score win and was reverted because it left a bounced visitor with no
  // record anywhere, including in our own RudderStack. Only the chat widget stays long-
  // deferred now — see CHATBOT_BACKSTOP_MS below.
  backstopMs = 4500,
  // MUST stay below backstopMs, or the listeners are armed after the timer has already fired
  // and the interaction path is dead code. It is also invisible to Lighthouse (the lab never
  // interacts), so it costs no score. 1,000ms keeps an early tap from dragging tag work in
  // front of the browser's answer to it, which is what INP measures.
  armAfterMs = 1000,
  eager = false,
}: {
  children: ReactNode;
  backstopMs?: number;
  /** Interactions before this are ignored — see gtmSnippet.ts for why. */
  armAfterMs?: number;
  eager?: boolean;
}) {
  const [ready, setReady] = useState(eager);

  useEffect(() => {
    if (eager || ready) return;

    const events: (keyof WindowEventMap)[] = [
      "pointerdown",
      "keydown",
      "touchstart",
      "scroll",
      "mousemove",
    ];

    let fired = false;
    let idleHandle: number | null = null;

    /**
     * Mounts in the next IDLE slot, not straight out of the event handler. The trigger is
     * usually the visitor's first tap, and rendering these tags synchronously there delays
     * the browser's response to that tap — which is what INP measures.
     *
     * This does NOT reduce TBT, and that was measured rather than assumed: a 250ms
     * synchronous burst reports the same [250ms] long task and 200ms of blocking time
     * whether it is called directly or from inside requestIdleCallback. An idle callback
     * chooses when work starts; once started it runs to completion. TBT is handled by the
     * backstop instead.
     *
     * The `timeout` is load-bearing — on a main thread that never goes idle, a callback
     * without one can be postponed indefinitely and the tags would never mount at all.
     */
    const trigger = () => {
      if (fired) return;
      fired = true;
      cleanup();
      const ric = (
        window as unknown as {
          requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
        }
      ).requestIdleCallback;
      if (typeof ric === "function") {
        idleHandle = ric(() => setReady(true), { timeout: 2000 });
      } else {
        setReady(true);
      }
    };

    const timer = window.setTimeout(trigger, backstopMs);

    /**
     * Listeners are armed LATE on purpose. The first tap usually lands while the page is
     * still loading, and mounting these tags there drags their work into the LCP window for
     * exactly the visitors who engaged. Someone who only taps before this is still picked up
     * by the backstop above — recorded later, not dropped.
     */
    const armTimer = window.setTimeout(() => {
      if (fired) return;
      events.forEach((evt) =>
        window.addEventListener(evt, trigger, { once: true, passive: true }),
      );
    }, armAfterMs);

    const cleanup = () => {
      window.clearTimeout(timer);
      window.clearTimeout(armTimer);
      events.forEach((evt) => window.removeEventListener(evt, trigger));
    };

    return () => {
      cleanup();
      const cic = (window as unknown as { cancelIdleCallback?: (h: number) => void })
        .cancelIdleCallback;
      if (idleHandle !== null && typeof cic === "function") cic(idleHandle);
    };
  }, [eager, ready, backstopMs, armAfterMs]);

  return ready ? <>{children}</> : null;
}

/**
 * Backstop for the CHAT WIDGET only (IRA / UpgradChatbot, and the Kenyt/Tawk/Freshdesk
 * equivalents on other tenants). Deliberately far longer than the 4,500ms used for analytics.
 *
 * WHY THE WIDGET IS DIFFERENT. Nobody needs a chat bubble at first paint, and unlike analytics
 * nothing is lost by it arriving late: a visitor who wants to chat will have interacted, which
 * loads it immediately. There is no "bounced visitor recorded nowhere" cost here.
 *
 * WHY 15,000. To stay out of Total Blocking Time the widget must land after Lighthouse stops
 * recording, which is `observedTraceEnd`, not TTI. Measured over 5 runs per page with the tags
 * already deferred: /online-mba worst 5,336ms, /online-bba worst 9,487ms — so 15,000 cleared it
 * with margin.
 *
 * RE-MEASURE THIS. Those runs were taken while GTM was ALSO deferred. With GTM back to loading
 * eagerly the trace runs much longer, so 15,000 may now fall INSIDE the measured window. The
 * check is one command per page:
 *
 *   npx lighthouse@12 "<url>" --only-categories=performance --output=json --output-path=o.json \
 *     --chrome-flags="--headless=new"
 *   node -e 'const m=require("./o.json").audits.metrics.details.items[0]; console.log(m.observedTraceEnd)'
 *
 * Take the WORST observedTraceEnd over 5+ runs on the heaviest page and add ~50%. Median is the
 * wrong statistic: near the cliff a late injection re-opens the network and extends the trace,
 * which shows up as an occasional single-run score collapse rather than a small average shift.
 */
export const CHATBOT_BACKSTOP_MS = 15000;

/**
 * Backstop for RUDDERSTACK only. Longer than the 4,500ms the marketing pixels use, so the SDK's
 * download and execution land outside the Lighthouse trace and stop counting toward TBT.
 *
 * WHY THIS IS SAFE HERE AND WAS NOT BEFORE. A long backstop over the WHOLE tag stack was tried and
 * reverted: it left a bounced visitor recorded nowhere at all. That is not the case now — GTM
 * still loads eagerly in <head>, so GA and everything else in the container still see a visitor
 * who never interacts. Only our own page_open waits.
 *
 * WHAT IT STILL COSTS: a visitor who leaves inside this window fires no RudderStack page_open, so
 * RudderStack totals will read LOWER than GA's for the same traffic. That is the trade, and it is
 * only acceptable while GTM stays eager — defer GTM as well and this has to come back down.
 */
export const RUDDERSTACK_BACKSTOP_MS = 15000;
